import { createFileRoute } from "@tanstack/react-router";
import { PEOPLE_ORIGIN, marketingHead } from "@/lib/marketing/seo";
import { SITE_NAME, breadcrumbs } from "@/lib/marketing/ppl-schema";
import { CORE_FILM, FILMS_ON, filmOgVideo, filmVideoObject } from "@/lib/marketing/film";
import { MarketingShell } from "@/components/marketing/site/marketing-shell";
import { MarketingCta } from "@/components/marketing/site/marketing-cta";
import { RecordSection } from "@/components/marketing/proof/record-section";
import { OperatingSystemHero } from "@/components/marketing/site/operating-system-hero";
import { CoreFilmSection } from "@/components/marketing/site/core-film-section";
import { JOURNEY_STAGES as JOURNEY_STAGE_DATA } from "@alm/domain/marketing/journey";
import { OsJourney } from "@/routes/v2/-components/os-journey";
import { OsSpotlights } from "@/routes/v2/-components/os-spotlights";
import { OsModules } from "@/routes/v2/-components/os-modules";
import { OsAria } from "@/routes/v2/-components/os-aria";
import { OsMedicaid } from "@/routes/v2/-components/os-medicaid";
import { OsTryIt } from "@/routes/v2/-components/os-try-it";
import { OsFaq } from "@/routes/v2/-components/os-faq";
import { OPERATING_SYSTEM_QUESTIONS } from "@/routes/v2/-lib/operating-system-data";

/* Published the way /pricing and /security publish theirs: its own FAQPage
   node, so a crawler that never runs the page still reads the answers. This
   node moved here with the questions from the home page. */
const FEATURES_FAQ_SCHEMA = {
  "@type": "FAQPage",
  mainEntity: OPERATING_SYSTEM_QUESTIONS.map((faq) => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: { "@type": "Answer", text: faq.a },
  })),
};

const FEATURES_SOFTWARE_SCHEMA = {
  "@type": "SoftwareApplication",
  name: SITE_NAME,
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  url: `${PEOPLE_ORIGIN}/operating-system`,
  /* Points at the Organization node the landing page publishes, so both
     application nodes on this surface resolve to one publisher rather than
     describing two companies with the same name. */
  publisher: { "@id": `${PEOPLE_ORIGIN}/#organization` },
  description:
    "The operating system for assisted living communities: hiring, sales, admissions, care, pharmacy, billing, compliance and maintenance in one secure platform.",
  // Journey order, like every other list on the site.
  featureList: JOURNEY_STAGE_DATA.map((s) => `${s.label}: ${s.headline}`),
};

function MarketingFeatures() {
  return (
    <MarketingShell>
      <OperatingSystemHero />

      <CoreFilmSection />

      {/* The record itself: one window, ten areas, five assurances. What the
          system is, before the stages and the modules say what it does. */}
      <RecordSection />

      <OsJourney />

      <OsSpotlights />

      <OsModules />

      <OsAria />

      <OsMedicaid />

      <OsTryIt />

      <OsFaq />

      <MarketingCta
        title="See it running in your community"
        subtitle="Book a 30-minute demo and explore every core module with our team."
      />
    </MarketingShell>
  );
}

export const Route = createFileRoute("/v2/operating-system")({
  component: MarketingFeatures,
  head: () => ({
    ...marketingHead({
      title:
        "Operating System: everything your community runs on, in one place | PEOPLE AI Workforce",
      description:
        "The operating system built for the humans who run a community: residents, care plans, eMAR and med pass, billing, admissions and Medicaid, on one record.",
      path: "/v2/operating-system",
      imageAlt: "PEOPLE AI Workforce. The operating system for assisted living communities",
      video: FILMS_ON ? filmOgVideo(CORE_FILM) : undefined,
      schema: [
        FEATURES_SOFTWARE_SCHEMA,
        FEATURES_FAQ_SCHEMA,
        ...(FILMS_ON ? [filmVideoObject(CORE_FILM, "/operating-system#core-film")] : []),
        breadcrumbs({ name: "Operating System", path: "/operating-system" }),
      ],
    }),
  }),
});
